import { useRef, useState } from "react";
import { api, apiErr, API as API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Link2, X, Loader2 } from "lucide-react";

/**
 * Image uploader with paste-URL fallback.
 * value: current URL (string)
 * onChange(url): called when value changes
 * kind: storage bucket label (product/logo/banner/etc.)
 */
export default function ImageUpload({ value, onChange, kind = "uploads", label, testId = "image-upload", aspect = "aspect-video" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Backend now returns an absolute URL (CDN or S3 public URL).
      // If it's a path-only fallback (starts with /api/), prepend backend origin.
      const url = data.absolute_url || data.url || "";
      const finalUrl = url.startsWith("http") ? url : `${API_BASE.replace(/\/api$/, "")}${url}`;
      onChange(finalUrl);
      toast.success("Uploaded");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <label className="text-sm font-medium text-slate-700">{label}</label>}
      {value ? (
        <div className={`relative ${aspect} rounded-md overflow-hidden border border-slate-200 bg-slate-50`}>
          <img src={value} alt="" className="w-full h-full object-cover" />
          <button
            type="button"
            data-testid={`${testId}-clear-btn`}
            onClick={() => onChange("")}
            className="absolute top-1.5 right-1.5 bg-white/90 rounded-full p-1 shadow hover:bg-white"
            aria-label="Clear image"
          >
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
      ) : (
        <div className={`${aspect} rounded-md border-2 border-dashed border-slate-200 grid place-items-center bg-slate-50 text-slate-400`}>
          <div className="text-xs">No image</div>
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testId}-file-input`}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload-btn`}
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
          {uploading ? "Uploading..." : "Upload"}
        </Button>
        <div className="relative flex-1">
          <Link2 className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            className="pl-7 text-xs"
            placeholder="…or paste image URL"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testId}-url-input`}
          />
        </div>
      </div>
    </div>
  );
}
